import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Checkbox, Input, Modal as AntModal, Select } from "antd";
import { generatedDocumentId } from "./matterPackageUtils";

// Name a multi-select so it can be prepared and questioned as one unit.
// "Save" rather than "group" is the point: a saved collection is a stable
// scope key, so the conversation about these twelve records can be
// reopened weeks later instead of dying with the browser session.

const Field = styled.div`
  margin-top: 14px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
`;

const Help = styled.p`
  margin: 8px 0 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;
`;

const ErrorText = styled.p`
  margin: 10px 0 0;
  color: #dc2626;
  font-size: 12px;
`;

const FileList = styled.ul`
  margin: 8px 0 0;
  padding: 8px 12px;
  max-height: 180px;
  overflow: auto;
  list-style: none;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;

  li {
    color: #374151;
    font-size: 12px;
    line-height: 1.6;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const MODE_NEW = "new";

export const collectionOptions = (collections) => [
  { value: MODE_NEW, label: "New collection…" },
  ...(collections || []).map((item) => ({
    value: String(item.id),
    label: `${item.name} (${item.member_count} document${
      item.member_count === 1 ? "" : "s"
    })`,
  })),
];

export default function SaveAsCollectionModal({
  open,
  documents,
  collections,
  loadingCollections,
  saving,
  onCancel,
  onSave,
}) {
  const [target, setTarget] = useState(MODE_NEW);
  const [name, setName] = useState("");
  const [prepare, setPrepare] = useState(true);
  const [error, setError] = useState("");

  const options = useMemo(() => collectionOptions(collections), [collections]);
  const count = documents?.length || 0;

  useEffect(() => {
    if (!open) return;
    setTarget(MODE_NEW);
    setName("");
    setPrepare(true);
    setError("");
  }, [open]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (target === MODE_NEW && !trimmed) {
      setError("Give the collection a name.");
      return;
    }
    setError("");
    const existing = (collections || []).find(
      (item) => String(item.id) === String(target)
    );
    try {
      await onSave({
        collectionId: target === MODE_NEW ? null : target,
        // Always the name the confirmation should use, whether it was
        // just typed or belongs to the collection being added to.
        name: target === MODE_NEW ? trimmed : existing?.name || "",
        prepare,
      });
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "Could not save the collection."
      );
    }
  };

  return (
    <AntModal
      title="Save as collection"
      open={open}
      onCancel={saving ? undefined : onCancel}
      onOk={handleSave}
      okText={saving ? "Saving…" : "Save collection"}
      okButtonProps={{
        disabled: saving || count === 0,
        "data-testid": "save-collection-submit",
      }}
      cancelButtonProps={{ disabled: saving }}
      destroyOnHidden
      width={560}
    >
      <Help>
        A collection is questioned as one unit, so you can ask whether a
        diagnosis appears anywhere across these records and get back the
        document and page that shows it.
      </Help>

      <Field>
        <Label htmlFor="collection-target">Add to</Label>
        <Select
          id="collection-target"
          data-testid="collection-target-select"
          value={target}
          loading={loadingCollections}
          options={options}
          onChange={setTarget}
          style={{ width: "100%" }}
        />
      </Field>

      {target === MODE_NEW && (
        <Field>
          <Label htmlFor="collection-name">Name</Label>
          <Input
            id="collection-name"
            data-testid="collection-name-input"
            value={name}
            placeholder="Post-service medical records"
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
            onPressEnter={handleSave}
          />
        </Field>
      )}

      <Field>
        <Label>
          {count} document{count === 1 ? "" : "s"}
        </Label>
        <FileList data-testid="collection-document-list">
          {(documents || []).map((doc) => (
            <li key={generatedDocumentId(doc)} title={doc.name}>
              {doc.name}
            </li>
          ))}
        </FileList>
      </Field>

      <Field>
        <Checkbox
          checked={prepare}
          data-testid="collection-prepare-checkbox"
          onChange={(event) => setPrepare(event.target.checked)}
        >
          Prepare these documents for AI now
        </Checkbox>
        <Help>
          Extraction and indexing run in the background. You can start asking
          questions as soon as the first document finishes; members still
          preparing are not searched until they are ready.
        </Help>
      </Field>

      {error && <ErrorText data-testid="collection-error">{error}</ErrorText>}
    </AntModal>
  );
}

SaveAsCollectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  documents: PropTypes.array,
  collections: PropTypes.array,
  loadingCollections: PropTypes.bool,
  saving: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
